import { MarketingPermissions } from "@redotech/account-common/permissions";
import {
  useLazyContextProvider,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomerSearch } from "@redotech/redo-merchant-app-common/client/shopify";
import { RedoMerchantDiscountsRpcClientContext } from "@redotech/redo-merchant-app-common/discounts-rpc-client-provider";
import { useTeamProviders } from "@redotech/redo-merchant-app-common/hooks/use-team-providers";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoMerchantSegmentsRpcClientContext } from "@redotech/redo-merchant-app-common/segments-rpc-client-provider";
import {
  CustomerActivityType,
  CustomerDataSource,
} from "@redotech/redo-model/marketing/segments/segment-types";
import { Provider } from "@redotech/redo-model/order";
import { ProductStatus } from "@redotech/redo-model/product";
import {
  AvailableProvidersContext,
  CollectionsContext,
  convertProductInfoToModalProduct,
  DiscountMethodsContext,
  DiscountMethodsContextI,
  DiscountsContext,
  ProductsContext,
  ShopifySegmentsContext,
} from "@redotech/redo-web/discounts/discount-selector";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { memo, ReactNode, useContext, useMemo } from "react";

export const DiscountsService = memo(function DiscountsService({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const discountsRpc = useRequiredContext(
    RedoMerchantDiscountsRpcClientContext,
  );
  const segmentsRpc = useRequiredContext(RedoMerchantSegmentsRpcClientContext);
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const merchantRpcClient = useContext(RedoMerchantRpcClientContext);

  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const canManageCampaigns = useHasPermission(
    MarketingPermissions.MANAGE_CAMPAIGNS,
  );
  const canUseDiscounts = canViewMarketing || canManageCampaigns;

  const teamProviders = useTeamProviders();

  const discounts = useLazyContextProvider(async (signal) => {
    if (!canUseDiscounts) {
      return [];
    }
    return discountsRpc.getDiscountsByTeam({}, { signal });
  });

  const collections = useLazyContextProvider(async (signal) => {
    if (!canUseDiscounts) {
      return [];
    }
    const collections = await segmentsRpc.getSegmentCollections(
      {
        dataStructureType: CustomerActivityType.COLLECTION_VIEWED_SHOPIFY,
        dataStructureFieldSource: CustomerDataSource.SHOPIFY,
      },
      { signal },
    );
    return collections.map((c) => {
      return {
        id: convertShopifyIdToGid(c.id, ShopifyIdType.Collection),
        name: c.title,
      };
    });
  });

  const products = useLazyContextProvider(async (signal) => {
    if (!canUseDiscounts) {
      return [];
    }
    const products = await segmentsRpc.getSegmentProducts(null, { signal });
    return products.map((p) => ({
      id: convertShopifyIdToGid(p.id, ShopifyIdType.Product),
      name: p.title,
    }));
  });

  const shopifySegments = useLazyContextProvider(async (signal) => {
    if (!canUseDiscounts) {
      return [];
    }
    try {
      return await segmentsRpc.getShopifySegments({}, { signal });
    } catch (error) {
      return [];
    }
  });

  const discountMethods = useMemo(() => {
    const methods: DiscountMethodsContextI = {
      createDiscount: async ({ discountConfiguration }) => {
        const discount = await discountsRpc.createDiscount({
          discountConfiguration,
        });
        discounts.invalidate();
        return discount._id;
      },
      deleteDiscount: async (discountId) => {
        await discountsRpc.deleteDiscount({ _id: discountId });
        discounts.invalidate();
      },
      updateDiscount: async ({ discountId, discountConfiguration }) => {
        await discountsRpc.updateDiscount({
          _id: discountId,
          configuration: discountConfiguration,
        });
        discounts.invalidate();
      },
      searchCustomers: async (searchText, signal) => {
        if (!canUseDiscounts || !searchText.trim()) {
          return [];
        }
        try {
          const response = await getCustomerSearch(merchantClient, {
            search: searchText,
            signal,
          });
          return response.customers || [];
        } catch (error) {
          if (!signal.aborted) {
            console.error("Customer search failed:", error);
          }
          return [];
        }
      },
      searchProducts: async ({ search, signal }) => {
        if (!merchantRpcClient || !canUseDiscounts || !search.trim()) {
          return [];
        }
        try {
          const response = await merchantRpcClient.searchProducts({
            searchString: search,
            provider: Provider.SHOPIFY,
            includeStatuses: [ProductStatus.ACTIVE, ProductStatus.DRAFT],
          });
          return (response?.products || []).map(
            convertProductInfoToModalProduct,
          );
        } catch (error) {
          if (!signal.aborted) {
            console.error("Product search failed:", error);
          }
          return [];
        }
      },
    };
    return methods;
  }, [
    discountsRpc,
    merchantClient,
    merchantRpcClient,
    canUseDiscounts,
    discounts,
  ]);

  return (
    <AvailableProvidersContext.Provider
      value={teamProviders.data?.available ?? []}
    >
      <DiscountMethodsContext.Provider value={discountMethods}>
        <CollectionsContext.internalContext.Provider value={collections}>
          <ProductsContext.internalContext.Provider value={products}>
            <DiscountsContext.internalContext.Provider value={discounts}>
              <ShopifySegmentsContext.internalContext.Provider
                value={shopifySegments}
              >
                {children}
              </ShopifySegmentsContext.internalContext.Provider>
            </DiscountsContext.internalContext.Provider>
          </ProductsContext.internalContext.Provider>
        </CollectionsContext.internalContext.Provider>
      </DiscountMethodsContext.Provider>
    </AvailableProvidersContext.Provider>
  );
});
